import React, { useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { yupResolver } from '@hookform/resolvers/yup'
import store from 'store'
import { Button, Input, Icon } from 'components/UI'
import ErrorMessageWithExclamation from '../../UI/ErrorMessageWithExclamation'
import VerificationCodeModal from '../../../pages/common/settings/Tabs/VerificationCodeModal'

import { resetSignInError, signIn } from '../../../routing/fetchingfunction/user/user.action'
import {
  selectErrorSignIn,
  selectUserIsSigningIn,
  selectRole,
  selectRegisterStep,
  selectUserData,
} from 'routing/fetchingfunction/user/user.reducer'

import { LoginUserScheme } from '../../../routing/utils/validators'
import { BACKEND_REGISTRATION_CODES } from 'constants/register'

const LoginForm = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const isLoading = useSelector(selectUserIsSigningIn)
  const error = useSelector(selectErrorSignIn)
  const isOtpError = error?.includes('OTP has been sent to registered number')
  const role = useSelector(selectRole)
  const registerStep = useSelector(selectRegisterStep)
  const userData = useSelector(selectUserData)
  const methods = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: yupResolver(LoginUserScheme),
  })

  const {
    formState: { errors },
    handleSubmit,
    control,
    getValues,
  } = methods

  const [passwordIsOpen, setPasswordIsOpen] = useState(false)

  const [userRole, setUserRole] = useState(store.get('user_role'))

  const onRoleChange = (role) => {
    setUserRole(role)
    store.set('user_role', role)
  }

  const onSubmit = (data) => {
    console.log(`[LoginForm] Submitting login with role: ${userRole}`)

    dispatch(
      signIn({
        ...data,
        // role: userRole,
      })
    )
  }

  const passwordToggle = () => {
    setPasswordIsOpen((prevState) => !prevState)
  }

  const handleCloseVerificationModal = () => {
    dispatch(resetSignInError())
  }

  const handleSubmitVerificationModal = (code) => {
    const singInData = getValues()

    dispatch(
      signIn({
        ...singInData,
        otp: code,
        role: userRole,
      })
    )
  }

  // Check for registration status after login
  useEffect(() => {
    // Only proceed if we have user data, role, AND valid tokens
    const accessToken = localStorage.getItem('access_token')
    const hasValidToken = accessToken && typeof accessToken === 'string' && accessToken.length > 20

    if (userData && role && hasValidToken) {
      // This effect should run only once after login success
      console.log(`[LoginForm] Processing successful login for ${role}`, {
        userId: userData.id,
        hasValidToken,
        registerStep,
        tokenLength: accessToken ? accessToken.length : 0,
      })

      // Add a small delay to ensure tokens are fully set in all places
      setTimeout(() => {
        // Check if registration is fully completed
        const registrationCompleted =
          registerStep === 'profile_completed' ||
          registerStep === 'completed' ||
          userData?.profile?.current_registration_step === 'profile_completed' ||
          userData?.profile?.current_registration_step === 'completed'

        if (registrationCompleted) {
          // Registration is complete, go to statistics page
          console.log('[LoginForm] Registration is complete, redirecting to statistics')
          navigate(`/${role}/statistic`, { replace: true })
          return
        }
      }, 300) // Small delay to ensure token propagation

      // Determine if profile has been filled out
      const isProfileFilled = userData?.profile?.birth_date && userData?.profile?.gender

      // Determine appropriate registration step
      let targetPath
      if (isProfileFilled) {
        // Profile is filled, move to the appropriate next step based on role
        if (role === 'student') {
          console.log('[LoginForm] Profile is complete, redirecting to student goals')
          targetPath = 'goals'
        } else {
          // tutor
          console.log('[LoginForm] Profile is complete, redirecting to tutor test')
          targetPath = 'pass-test'
        }
      } else {
        // Profile not filled, direct to profile-update
        console.log('[LoginForm] Profile is incomplete, redirecting to profile-update')
        targetPath = 'profile-update'
      }

      // Set the intended target in session storage to avoid loops
      sessionStorage.setItem('last_navigation_target', `/${role}/register/${targetPath}`)

      // Use replace instead of push to avoid browser back issues
      navigate(
        `/${role}/register/${targetPath}?${(registerStep === BACKEND_REGISTRATION_CODES.TUTOR_KYC || registerStep === BACKEND_REGISTRATION_CODES.TUTOR_CREATE_BANNER) && 'test-step=7'}`,
        { replace: true }
      )
    }
  }, [userData, role, registerStep, navigate])

  useEffect(() => {
    return () => {
      dispatch(resetSignInError())
    }
  }, [dispatch])

  return (
    <div className="bg-[#FEEBDD] rounded-lg text-[#F67443] max-w-[800px] w-full px-[148px] py-16 mx-auto my-16 flex flex-col items-center justify-start">
      <h1 className="text-4xl font-semibold mb-[32px]">Log in</h1>

      {!isOtpError && error && (
        <ErrorMessageWithExclamation text={error} className="mr-auto mb-4" />
      )}

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <div className="mb-[20px]">
            <label className="text-sm font-medium mb-[6px]" htmlFor="email">
              Email
            </label>

            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <Input
                  id="email"
                  wrapperClassName="w-full"
                  placeholder="Enter your email here"
                  type="text"
                  inputMode="email"
                  {...field}
                />
              )}
            />

            {errors.email && <ErrorMessageWithExclamation text={errors.email.message} />}
          </div>
          <div>
            <label className="text-sm font-medium mb-[6px]" htmlFor="password">
              Password
            </label>

            <Controller
              control={control}
              name="password"
              render={({ field }) => (
                <Input
                  id="password"
                  wrapperClassName="w-full"
                  placeholder="Password"
                  type={passwordIsOpen ? 'text' : 'password'}
                  rightAddon={
                    <Icon
                      name={passwordIsOpen ? 'eye' : 'eye-closed'}
                      className="mr-2 cursor-pointer"
                    />
                  }
                  rightAddonClick={passwordToggle}
                  rightAddonAriaLabel="Toggle password visibility"
                  {...field}
                />
              )}
            />

            {errors.password && <ErrorMessageWithExclamation text={errors.password.message} />}
          </div>

          <Button
            className="mt-[24px] w-full h-[44px] font-semibold text-base"
            type="submit"
            isLoading={isLoading}
          >
            Login
          </Button>

          <div className="text-center mt-[24px]">
            <Link
              to="/forgot-password"
              className="text-sm inline-block bg-transparent text-primary-100 hover:text-primary-100 font-bold hover:underline"
            >
              I Forgot My Password
            </Link>
          </div>
        </form>
      </FormProvider>

      <VerificationCodeModal
        open={isOtpError}
        isLoading={isLoading}
        requestError={isOtpError ? null : error}
        onClose={handleCloseVerificationModal}
        onSubmit={handleSubmitVerificationModal}
      />
    </div>
  )
}

export default React.memo(LoginForm)
